<template>
  <BaseModal :show="show" @close="$emit('close')">
    <template #title>
      Wybierz kategorię
    </template>
    
    <div class="space-y-3">
      <!-- Search Input -->
      <div class="relative">
        <div class="absolute left-3 top-1/2 -translate-y-1/2">
          <font-awesome-icon icon="search" class="text-slate-400" />
        </div>
        <input 
          ref="inputEl" 
          v-model.trim="search" 
          type="text" 
          class="w-full h-10 pl-10 pr-4 border border-blue-200 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors" 
          placeholder="Szukaj kategorii po nazwie" 
        />
      </div>

      <!-- Results -->
      <div class="max-h-80 overflow-y-auto">
        <div v-if="loading" class="text-center py-6">
          <div class="inline-block w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p class="text-slate-600 mt-2 text-sm">Wyszukiwanie...</p>
        </div>
        <div v-else-if="search.trim().length < 2" class="text-center py-6 text-slate-500">
          <p class="text-sm">Zacznij pisać, aby wyszukać</p>
        </div>
        <div v-else-if="results.length > 0" class="space-y-1">
          <div 
            v-for="category in results" 
            :key="category.id"
            class="flex items-center justify-between p-2 px-4 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
          >
            <div>
              <p class="font-medium text-slate-800 text-sm mb-0">{{ category.name }}</p>
              <p v-if="category.parent_name" class="text-xs text-slate-500 mt-0.5">
                Nadrzędna: {{ category.parent_name }}
              </p>
            </div>
            <button 
              class="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors" 
              type="button" 
              @click="$emit('select', category)"
            >
              Wybierz
            </button>
          </div>
        </div>
        <div v-else class="text-center py-6 text-slate-500">
          <p class="text-sm">Nie znaleziono kategorii</p>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import BaseModal from '../../../shared/components/BaseModal.vue';
import { categoryApi } from '../services/categories.api';
import { useApiConfig } from '../../../composables/useApi';

const props = defineProps<{ show: boolean }>();
const emit = defineEmits<{ (e:'close'):void; (e:'select', item:{ id:number; name:string; parent_name?: string|null }):void }>();

const search = ref('');
const inputEl = ref<HTMLInputElement|null>(null);
const loading = ref(false);
const results = ref<Array<{ id:number; name:string; parent_name?: string|null }>>([]);
const api = useApiConfig();

function debounce<F extends (...args: any[]) => any>(fn: F, delay = 300) {
  let t: any;
  return (...args: Parameters<F>) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
}

const debouncedSearch = debounce(async () => {
  const q = search.value.trim();
  if (q.length < 2) {
    results.value = [];
    loading.value = false;
    return;
  }
  loading.value = true;
  try {
    results.value = await categoryApi.searchWithConfig(api, q);
  } catch (e) {
    console.error('Category search failed', e);
    results.value = [];
  } finally {
    loading.value = false;
  }
}, 300);

watch(search, () => debouncedSearch());
watch(() => props.show, async (v) => {
  if (v) {
    await nextTick();
    inputEl.value?.focus();
    if (search.value) debouncedSearch();
  }
});
</script>
